import { Button, Cascader, Form, FormProps, Input, InputNumber, Space, Row, Col, Radio } from 'antd';
import { ReactNode } from 'react';
import { getMonthDaySelectOptions } from 'sb/shared/Helpers';
import { UploadFile } from './UploadFile';
import './VendorForm.scss';
import { FileUploadResult } from 'sb/models/Files';
import { useVendorApi } from 'sb/api/vendor';
import { DomainLogo } from './DomainLogo';

interface Props extends FormProps {
  loading?: boolean;
  onCancel?: () => void;
  addOrEditVendor: boolean;
  vendor?: any;
  selectedVendor?: number;
  headerActions?: ReactNode;
}

export const VendorForm: React.FC<Props> = ({
  loading,
  onCancel,
  onFinish,
  addOrEditVendor,
  vendor,
  selectedVendor: _selectedVendor,
  headerActions,
  ...rest
}) => {
  // API
  const { updateSupplierLogoFilename } = useVendorApi();

  const onSaveLogo = async (res: FileUploadResult) => {
    if (vendor?.id && res?.key && res?.signedUrl) {
      await updateSupplierLogoFilename(vendor?.id, res.key);
    }
  };

  return (
    <Form
      onFinish={onFinish}
      disabled={!addOrEditVendor}
      // layout="vertical"
      size="middle"
      requiredMark={false}
      className={addOrEditVendor ? 'vendor-form enable-form text-lg' : 'vendor-form hide-appearance'}
      {...rest}
    >
      <Row gutter={[24, 16]} className="border-b" align="top">
        <Col flex="none" className="py-5 pt-0">
          <UploadFile
            buttonType="link"
            onUpload={onSaveLogo}
            className={'upload-btn upload-button ant-input w-fit max-w-52'}
            uploadLabel={
              <div className="text-l h-full p-2">
                <div className="flex h-[60px] w-[105px] items-center justify-center rounded bg-gray-50">
                  <DomainLogo
                    name={vendor?.name ?? 'Supplier'}
                    domainOrWebsite={vendor?.domain}
                    className="max-h-[60px] max-w-[105px] object-contain"
                    fallbackClassName="flex h-12 w-12 items-center justify-center rounded-md bg-blue-100 text-base font-bold text-blue-600"
                  />
                </div>
              </div>
            }
          />
        </Col>
        <Col flex="auto" className="border-0 pt-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Form.Item
                name="name"
                label="Name"
                className={'hide-label !border-0 !pb-0 !pt-0'}
                rules={[{ required: true, message: 'Please input vendor name' }]}
              >
                <Input className="supplier-title" placeholder="Supplier Name" />
              </Form.Item>
            </div>
            {headerActions ? <div className="pt-1">{headerActions}</div> : null}
          </div>
          <Form.Item name="profile" className={'hide-label !mb-[-1px] !border-0'} label="Profile">
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} placeholder="Profile" />
          </Form.Item>
        </Col>
      </Row>
      <div className="grid grid-cols-2 gap-x-8">
        <Form.Item name="industry" label="Industry">
          <Input />
        </Form.Item>
        <Form.Item name="sub_industry" label="Sub-Industry">
          <Input />
        </Form.Item>
        <Form.Item name="domain" label="Domain">
          <Input />
        </Form.Item>
        <Form.Item name="symbol" label="Stock Symbol">
          <Input />
        </Form.Item>
        <Form.Item name="annual_revenue" label="Revenue">
          <InputNumber className="w-full" />
        </Form.Item>
        <Form.Item name="fiscal_year_end" label="Fiscal Year End">
          <Cascader options={getMonthDaySelectOptions()} displayRender={(label) => label?.join(' ')} />
        </Form.Item>
        <Form.Item name="salesforce_id" label="Salesforce ID">
          <Input />
        </Form.Item>
        <Form.Item name="is_pinpoint" label="Is Pinpoint">
          {!addOrEditVendor ? (
            <div>{vendor?.is_pinpoint ? 'Yes' : 'No'}</div>
          ) : (
            <Radio.Group>
              <Space direction="horizontal">
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>
          )}
        </Form.Item>
      </div>

      <Form.Item className="text-right form-actions">
        <Space>
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
